import {
  DashboardHeader,
  StatCardItem,
  StatsCard,
  UiTable,
  WhiteCard,
  UiButton,
  UiBadge,
  UiDetailButton,
  UiEditButton,
  UiValidateCommandeButton,
} from "@/components";
import {
  CommandeFilterDataModel,
  CommandeFilterForm,
} from "@/components/molecules/commande";
import { Head } from "@/components/seo";
import { useCommande } from "@/composables/commande";
import { Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useCommandeStore } from "@/context/stores/commande.store";
import { formatAppDate, formatAppDateTime } from "@/utils/dates";
import { UserTypes } from "@/composables/users";
import { useAuth } from "@/composables/auth";

export default function DashboardCommandes() {
  const navigate = useNavigate();
  const {
    loading,
    fetchCommandes,
    waitingCommandeAction,
    getCommandeStatusBadgeCode,
  } = useCommande();
  const { currentUserAuthorized } = useAuth();

  const { filterForm, sort, setFilterForm, setSort } = useCommandeStore();
  const commandes = useCommandeStore((state) => state.paginatedItems.content);
  const paginatedItems = useCommandeStore((state) => state.paginatedItems);

  useEffect(() => {
    fetchCommandes();
  }, [filterForm, sort]);

  const handleFilter = (data: CommandeFilterDataModel) => {
    setFilterForm(data);
  };

  const columns = [
    {
      accessor: "auteur",
      title: "Auteur",
      render: (row: any) => {
        return (
          <span>
            {row.auteur?.nom ?? ""} {row.auteur?.prenom ?? ""}
          </span>
        );
      },
    },
    {
      accessor: "fournisseur",
      title: "Fournisseur",
      render: (row: any) => {
        return (
          <span>
            {row.fournisseur?.nom ?? ""} {row.fournisseur?.prenom ?? ""}
          </span>
        );
      },
    },
    {
      accessor: "villeLivraison",
      title: "Ville de livraison",
    },
    {
      accessor: "dfs",
      sortable: true,
      title: "Date de fabrication souhaitée",
      sortKey: "dfs",
      render: (row: any) => {
        return <span>{formatAppDate(row.dfs ?? "")}</span>;
      },
    },
    {
      accessor: "dateCreation",
      title: "Date de création",
      sortKey: "dateCreation",
      sortable: true,
      render: (row: any) => {
        return <span>{formatAppDateTime(row.dateCreation ?? "")}</span>;
      },
    },
    {
      accessor: "ddm",
      title: "Date de modification",
      sortable: true,
      sortKey: "ddm",
      render: (row: any) => {
        return <span>{formatAppDateTime(row.ddm ?? "")}</span>;
      },
    },
    {
      accessor: "statut",
      title: "Statut",
      render: (row: any) => {
        return (
          <UiBadge
            variant={getCommandeStatusBadgeCode(row)}
            className="block text-center"
          >
            {row.statut}
          </UiBadge>
        );
      },
    },
    {
      accessor: "campagne",
      title: "Campagne",
      render: (row: any) => {
        return (
          <Link
            to={`/dashboard/campagnes/${row.campagne?.reference ?? ""}`}
            className="text-blue-500"
          >
            Détail Campagne
          </Link>
        );
      },
    },
    {
      accessor: "actions",
      title: "Actions",
      render: (row: any) => {
        return (
          <div className="flex gap-2 flex-wrap">
            <UiDetailButton
              onClick={() => navigate(`/dashboard/commandes/${row.reference}`)}
            />
            {![
              "LIVRE",
              "EN_COUR_FABRICATION",
              "EN_COURS_LIVRAISON",
              "REFUSER",
            ].includes(row.statut) && (
              <UiEditButton
                onClick={() =>
                  navigate(`/dashboard/commandes/edit/${row.reference}`)
                }
              />
            )}
            {row.statut === "INITIER" &&
              currentUserAuthorized([UserTypes.ADMINISTRATEUR]) && (
                <UiValidateCommandeButton
                  confirmTitle="Validation ou rejet de la commande"
                  confirmMessage="Voulez vous valider la commande ?"
                  okLabel="Accepter"
                  cancelLabel="Refuser"
                  onCancel={() => waitingCommandeAction(row.reference, false)}
                  onClick={() => waitingCommandeAction(row.reference, true)}
                />
              )}
          </div>
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    fetchCommandes({ page: page - 1 });
  };

  const handleSortStatusChange = (sortStatus: any) => {
    setSort(sortStatus);
  };

  return (
    <>
      <Head title="Commandes | Jourgui"></Head>
      <DashboardHeader subTitle="Bienvenue aux commandes" />

      <div>
        <StatsCard>
          <StatCardItem
            title="Commandes livrées"
            value="89935"
            evolution="+1.01% this week"
          />
          <StatCardItem
            title="Commandes en cours"
            value="23283.5"
            evolution="+1.01% this week"
          />
          <StatCardItem
            title="Boucles commandées"
            value="46827"
            direction="down"
            evolution="-0.09% this week"
          />
          <StatCardItem
            title="Boucles distribuées"
            value="23283.5"
            evolution="+1.01% this week"
          />
          <StatCardItem
            title="Foyers épidémiques"
            value="124"
            evolution="+1.01% this week"
          />
          <StatCardItem
            title="Budget alloué"
            value="1926336562"
            evolution="+1.01% this week"
          />
        </StatsCard>
      </div>

      <div className="py-4">
        <WhiteCard
          styles={{ borderRadius: "30px" }}
          headerSlot={
            <div className="flex items-center justify-between">
              <UiButton
                onClick={() => navigate("/dashboard/commandes/add")}
                startIcon={<Plus />}
              >
                Ajouter une commande
              </UiButton>

              <CommandeFilterForm
                defaultValue={{
                  status: "",
                  creationDate: "",
                  dfs: "",
                }}
                onChange={handleFilter}
              />
            </div>
          }
        >
          <div className="py-6">
            <UiTable
              columns={columns}
              rows={commandes}
              loading={loading}
              totalElements={paginatedItems.totalElements}
              totalPages={paginatedItems.totalPages}
              pageSize={paginatedItems.size}
              page={paginatedItems.number + 1}
              onPageChange={handlePageChange}
              onSortStatusChange={handleSortStatusChange}
            />
          </div>
        </WhiteCard>
      </div>
    </>
  );
}
